'use client';

import { fetchMeClient } from '@/lib/me-client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

/**
 * Mobile-only fixed bottom navigation.
 *
 * CRO-63 P1 #5 (2026-06-04) — first-version mobile nav is the same 5 core
 * tabs as the prototype: 概览 / 知识库 / 训练 / 设置 / 试聊. 发现 (Discover)
 * is gated by the orchestrator feature flag (off in production V1, on in
 * staging) and slots in as a 6th tab when enabled. Old 3-tab model
 * (概览 / 训练 / 发现) was unreachable for 知识库 / 分身设置 / 试聊 — the
 * three things the V1 onboarding wants users to do on mobile.
 *
 * Visible only for logged-in users on screens < md (md:hidden). Fetches
 * /api/me once to know the role + flags; renders nothing for consumers
 * (single-tab nav is pointless) and for anonymous visitors.
 *
 * Page content gets bottom padding via the spacer at the end of body in
 * app/layout.tsx — not via CSS env(safe-area-inset-bottom) here, because the
 * nav itself uses safe-area padding inside.
 */
type Me = {
  user: { role: string };
  flags?: { writing?: boolean; discover?: boolean };
};

// 2026-06-12 — emoji → Lucide migration (per docs/icon-handoff.md). 把每个 tab
// 的 icon 字段从字符串 emoji 换成 Lucide 组件构造器, render 时直接 <Icon size={20} />。
import type { LucideIcon } from 'lucide-react';
import { BookOpen, Globe2, Home, MessageCircle, PenLine, Settings, Sliders } from 'lucide-react';

type Tab = {
  href: string;
  label: string;
  icon: LucideIcon;
  matches: (path: string) => boolean;
};

const ECON_TABS: Tab[] = [
  { href: '/studio', label: '概览', icon: Home, matches: (p) => p === '/studio' },
  {
    href: '/studio/vault',
    label: '知识库',
    icon: BookOpen,
    matches: (p) => p.startsWith('/studio/vault'),
  },
  { href: '/studio/train', label: '训练', icon: PenLine, matches: (p) => p === '/studio/train' },
  {
    href: '/studio/persona',
    label: '设置',
    icon: Sliders,
    matches: (p) => p.startsWith('/studio/persona') || p.startsWith('/studio/soul-tuning'),
  },
  {
    href: '/studio/launch',
    label: '试聊',
    icon: MessageCircle,
    matches: (p) => p.startsWith('/studio/launch'),
  },
];

// Amy CRO-132 P2-6 (2026-06-29) — 撰写 (Writing Studio) was missing from
// the mobile bottom nav while PC top nav had it. Mobile economists who
// were directed to write a report had no entry point. Added as a
// flag-gated tab (matches PC behavior — staging shows it, prod hides
// it via `flags.writing` until the writing flow is GA).
const WRITING_TAB: Tab = {
  href: '/studio/studio',
  label: '撰写',
  icon: PenLine,
  matches: (p) => p.startsWith('/studio/studio'),
};

// 发现 (Discover) — gated by env.ENABLE_DISCOVER on the orchestrator.
// Off on production V1 (per CRO-63 P0 #4), on for staging QA.
const DISCOVER_TAB: Tab = {
  href: '/discover',
  label: '发现',
  icon: Globe2,
  matches: (p) => p === '/discover' || p.startsWith('/e/'),
};

// V2.11 (2026-05-23) — platform admins also get a 「管理」 entry. Without
// it, the only path to /platform on mobile was the ProfileMenu admin
// switch — too easy to miss, and admins reported they couldn't see
// pending economist applications on their phone.
const ADMIN_TAB: Tab = {
  href: '/platform',
  label: '管理',
  icon: Settings,
  matches: (p) => p.startsWith('/platform'),
};

export function MobileBottomNav({
  initialRole = null,
  initialDiscoverEnabled = null,
}: {
  initialRole?: string | null;
  initialDiscoverEnabled?: boolean | null;
}) {
  const pathname = usePathname();
  const [role, setRole] = useState<string | null>(initialRole);
  // CRO-63 P0 #4 (2026-06-04) — Discover gate from feature flags.
  // null = still loading; default behaviour while loading is "hide" so
  // production users don't see a flash of the 发现 tab before the
  // /api/me response strips it.
  const [discoverEnabled, setDiscoverEnabled] = useState<boolean | null>(initialDiscoverEnabled);
  // Amy CRO-132 P2-6 (2026-06-29) — writing flag mirrors discover.
  const [writingEnabled, setWritingEnabled] = useState<boolean | null>(null);

  // V3.4 (2026-06-03 P2-C) — share /api/me with TopBarClient + ProfileMenu
  // via fetchMeClient(). All first-paint chrome readers now collapse into
  // one /api/me round-trip.
  //
  // V3.4 (2026-06-04) — root layout now passes initialRole from the
  // cached server-side getMe() call, so when it's already populated we
  // skip the client-side recovery fetch. Still fetches when seed is
  // null (the next-on-pages stale-layout corner case).
  useEffect(() => {
    if (initialRole && initialDiscoverEnabled !== null && writingEnabled !== null) return;
    let cancelled = false;
    fetchMeClient().then((body) => {
      if (cancelled) return;
      setRole(body?.user?.role ?? null);
      const flags = (body as { flags?: { discover?: boolean; writing?: boolean } } | null)?.flags;
      setDiscoverEnabled(flags?.discover === undefined ? true : !!flags.discover);
      setWritingEnabled(flags?.writing === undefined ? true : !!flags.writing);
    });
    return () => {
      cancelled = true;
    };
  }, [initialRole, initialDiscoverEnabled, writingEnabled]);

  // Render nothing for anonymous, consumer, pending — they don't need the
  // multi-tab nav. Economist + platform_admin get it.
  if (role !== 'economist' && role !== 'platform_admin') return null;

  const tabs: (Tab & { comingSoon?: boolean })[] = [...ECON_TABS];
  // Amy CRO-132 P2-6 — slot 撰写 between 试聊 (last in ECON_TABS) and 发现
  // so the visual ordering matches PC top nav. Hidden when writing flag is
  // off (prod V1) to keep parity with TopBarClient.
  if (writingEnabled !== false) tabs.push(WRITING_TAB);
  // 2026-06-05 — show 发现 even when the flag is off; mark it coming-soon
  // so the icon tile still renders but routes to /coming-soon when tapped.
  tabs.push({ ...DISCOVER_TAB, comingSoon: !discoverEnabled });
  if (role === 'platform_admin') tabs.push(ADMIN_TAB);
  const colsClass =
    tabs.length >= 8
      ? 'grid-cols-8'
      : tabs.length === 7
        ? 'grid-cols-7'
        : tabs.length === 6
          ? 'grid-cols-6'
          : tabs.length === 5
            ? 'grid-cols-5'
            : tabs.length === 4
              ? 'grid-cols-4'
              : 'grid-cols-3';

  return (
    <nav
      aria-label="主导航"
      // V2.11 (2026-05-21n) — tighter buttons + reduced home-indicator
      // padding. Previous version (py-2.5, gap-1, text-xl) had ~94px
      // total nav height + felt bottom-heavy. Now ~76px with the same
      // touch-target size: py-2 + gap-0.5 + text-lg icon. The
      // pb-[max(env-0.25rem, 0.25rem)] pulls buttons 4px closer to the
      // home indicator while still respecting iOS HIG clearance.
      className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white/95 backdrop-blur border-t border-soft pb-[max(calc(env(safe-area-inset-bottom)-0.25rem),0.25rem)]"
    >
      <ul className={`grid ${colsClass}`}>
        {tabs.map((t) => {
          const isActive = t.matches(pathname ?? '');
          const href = t.comingSoon ? `/coming-soon?from=${encodeURIComponent(t.href)}` : t.href;
          return (
            <li key={t.href}>
              <Link
                href={href}
                // V3.4 (2026-06-04) — full prefetch for the 3-4 tab
                // mobile bottom nav so phones get the same instant-tab
                // experience as PC after first paint.
                prefetch
                // 2026-06-25 W3 (Codex M38-P2-1) — 11px → 12px for CJK
                // legibility. This is the project's single mobile bottom-nav
                // surface, so the "shared token" requirement is satisfied
                // by it being the sole call site. If a second nav surfaces
                // anywhere, extract into a token rather than copy 12px.
                className={`flex flex-col items-center justify-center gap-0.5 py-2 text-[12px] transition active:bg-soft/40 ${
                  t.comingSoon
                    ? 'text-dusk/50'
                    : isActive
                      ? 'text-warmth font-medium'
                      : 'text-dusk hover:text-ink'
                }`}
              >
                <t.icon aria-hidden size={20} strokeWidth={1.75} />
                {t.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
